import React, { useMemo } from 'react';
import { Box, Tab, Tabs, Typography, Card, CardContent, Chip, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import InterBeneficiaryConfigPage from './InterBeneficiaryConfigPage';
import InterWebhooksConfigPage from './InterWebhooksConfigPage';

type BankSection = 'beneficiary' | 'webhooks';

function normalizeBankSection(raw: string | null): BankSection {
  const v = (raw || '').toLowerCase();
  if (v === 'webhooks' || v === 'webhook') return 'webhooks';
  return 'beneficiary';
}

function TabPanel(props: { value: BankSection; section: BankSection; children: React.ReactNode }) {
  const { value, section, children } = props;
  if (value !== section) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const BankIntegrationConfigPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const bankSection = useMemo(
    () => normalizeBankSection(searchParams.get('bankSection')),
    [searchParams]
  );

  const handleSectionChange = (_: React.SyntheticEvent, next: BankSection) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('tab', 'bank');
      p.set('bankSection', next);
      return p;
    });
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Integração bancária
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configurações da integração com o Banco Inter: beneficiário usado na emissão de
              cobranças e webhooks que atualizam pagamentos no vover-billing.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label="Banco Inter" />
              <Chip size="small" variant="outlined" label="Beneficiário" />
              <Chip size="small" variant="outlined" label="Webhooks" />
              <Chip size="small" variant="outlined" label="Escopo global" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Tabs
        value={bankSection}
        onChange={handleSectionChange}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
        aria-label="Seções da integração bancária"
      >
        <Tab label="Beneficiário — dados e recebimentos" value="beneficiary" />
        <Tab label="Webhooks — notificações do Inter" value="webhooks" />
      </Tabs>

      <TabPanel value={bankSection} section="beneficiary">
        <InterBeneficiaryConfigPage embedded />
      </TabPanel>
      <TabPanel value={bankSection} section="webhooks">
        <InterWebhooksConfigPage embedded />
      </TabPanel>
    </Box>
  );
};

export default BankIntegrationConfigPage;
